import { AGRONOMY_PAYMENT_DUE_COUNT_BY_TERMS, AGRONOMY_PAYMENT_DUE_FIELDS } from 'appConstants';
import dayjs from 'dayjs';
import type { IValidationField } from 'hooks/useValidations/types';
import { getFixedT } from 'i18next';
import * as yup from 'yup';

const isFilledDate = (value?: string | null): value is string =>
  Boolean(value) && dayjs(value).isValid();

export default function agreementPaymentDue({ label = '', context = {} }: IValidationField) {
  const t = getFixedT(null, 'CreateAgronomyAgreement', 'CustomerDetails');
  const index: number = context.index ?? 0;
  const previousField = AGRONOMY_PAYMENT_DUE_FIELDS[index - 1];

  return yup
    .string()
    .label(label)
    .nullable()
    .default('')
    .when('paymentTerms', {
      is: (paymentTerms: string) =>
        (AGRONOMY_PAYMENT_DUE_COUNT_BY_TERMS[paymentTerms] ?? 0) > index,
      then: (schema) =>
        schema
          .required(t('errors.required'))
          .isValidDate(t('errors.invalidDate', { label }))
          .test('afterStartDate', t('errors.minPaymentDue'), (value, { parent: { startDate } }) => {
            if (!isFilledDate(value) || !isFilledDate(startDate)) {
              return true;
            }

            return dayjs(value).isSameOrAfter(dayjs(startDate), 'day');
          })
          .test('beforeEndDate', t('errors.maxPaymentDue'), (value, { parent: { endDate } }) => {
            if (!isFilledDate(value) || !isFilledDate(endDate)) {
              return true;
            }

            return dayjs(value).isSameOrBefore(dayjs(endDate), 'day');
          })
          .test(
            'afterPreviousPaymentDue',
            t('errors.paymentDueAfterPrevious'),
            (value, { parent }) => {
              const previous = previousField ? parent[previousField] : undefined;
              if (!isFilledDate(value) || !isFilledDate(previous)) {
                return true;
              }

              return dayjs(value).isAfter(dayjs(previous), 'day');
            },
          ),
      otherwise: (schema) => schema,
    });
}
